/* eslint-disable @next/next/no-img-element */
import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import InputMarkdown from "@components/molecules/InputMarkdown";
import InputTextPV from "@components/molecules/InputTextPV";
import useRequiredFieldsHook from "@hooks/useRequiredFieldsHook";
import useValidityFieldsHook from "@hooks/useValidityFieldsHook";
import { CloudService } from "@prisma/client";
import { vmWareProducts } from "@shared/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createEvents } from "actions/events/create-events";
import { deleteEventById } from "actions/events/delete-event";
import { updateEvents } from "actions/events/update-events";
import axios from "axios";
import SelectPV from "components/atoms/SelectPV/SelectPV";
import DeleteButtonPopup from "components/molecules/DeleteButtonPopup";
import InputDate from "components/molecules/InputDate";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import Loader from "componentsV2/atoms/Loadings/Loader";
import EventModeration from "componentsV2/organisms/EventModeration";
import EventsContext from "context/EventsListingsProvider";
import useOutsideClickMultiple from "hooks/useOutsideClickMultiple";
import useRouterStuff from "hooks/useRouterStuff";
import Dropzone from "react-dropzone";
import { useTranslation } from "react-i18next";
import { colors } from "shared/events";
import {
  addTimeToDate,
  arraysAreEqualByDiffs,
  getHoursAndMinutesFromISODate,
  getNewDate,
  getTimeFromDate,
  getTimezonesData,
  resizedImage,
} from "shared/utils";
import { toast } from "sonner";
import { TAGS } from "utils/caching";

import ColorSymbol from "../../../../../public/images/color-symbol.svg";
import ConfirmDeleteModal from "../../ConfirmDeleteModal";
import {
  checkError,
  checkRequiredFields,
  checkValidityFields,
  getComponentRequiredFields,
  getComponentsValidityFields,
  logicForRequiredFields,
  logicForValidityFields,
} from "../utils";
import styles from "./style.module.scss";

const MOD_PERMISSIONS = ["FULL", "EDIT"];
const DEFAULT_IMAGE = "/images/event-broadcom-large.png";
const DEFAULT_PARTNER_IMAGE = "/images/event_banner_def.svg";
const DELETE_IMAGE = "/images/delete-icon-blue.svg";
const DEFAULT_WEBCAST = "/images/event-webcast-large.png";
const DEFAULT_CONFERENCE = "/images/event-conference-large.png";
const DEFAULT_USER_GROUP = "/images/event-user-group-large.png";
const DEFAULT_TRAINING = "/images/event-training-large.png";
const DEFAULT_CSP = "/images/csp-event-large.png";
const MAX_WORDS_TAGLINE = 10;

export default function EventModalModeration({
  changeSetOpen,
  isOpen,
  isCreate = false,
  isSiteAdminPage = false,
  isModeration = false,
  editEvents = false,
  providerId,
  eventTypes,
  regions,
  products,
  cloudServices,
  partnerId,
  partnerName,
  partnerSlug,
  userPermissions,
  newEventsCreate,
  setNewEventsCreate = (a) => {},
  initialEventState,
}: {
  changeSetOpen: (a: boolean) => void;
  isOpen: boolean;
  isCreate?: boolean;
  isSiteAdminPage?: boolean;
  isModeration?: boolean;
  editEvents?: boolean;
  providerId?: number;
  eventTypes?: any[];
  regions?: any[];
  products?: any[];
  cloudServices?: any[];
  partnerId?: string;
  partnerName?: string;
  partnerSlug?: string;
  userPermissions?: any;
  newEventsCreate?: any[];
  setNewEventsCreate?: (a: any[]) => void;
  initialEventState?: any;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [tabSelected, setTabSelected] = useState(0);
  const ref = useRef(undefined);
  const [modContentIsOpen, setModContentIsOpen] = useState(false);
  const dropzoneRef: any = createRef();
  const [rerender, setRerender] = useState(false);
  const {
    allTimezones,
    selectedEvent,
    setSelectedEvent,
    usersTimezoneLocation,
    requiredFields,
    setRequiredFields,
    fieldsValidity,
    setFieldsValidity,
  } = useContext(EventsContext);

  const { queryParams } = useRouterStuff();
  const [areThereChanges, setAreThereChanges] = useState(
    isCreate ? true : false
  );
  const [showErrors, setShowErrors] = useState(false);
  const getPermissionsByEventType = (): string => {
    if (isSiteAdminPage) return "FULL";
    if (providerId) return userPermissions.providerEvents;
    if (partnerId) return userPermissions.partnerEvents;

    return "VIEW";
  };

  const onlyLevel2isRelevant = (level2) => vmWareProducts.includes(level2);

  const [popupOpen, setPopupOpen] = useState(false);

  const [eventImage, setEventImage] = useState(null);
  const [selectedEventBanner, setSelectedEventBanner] = useState(DEFAULT_IMAGE);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      setIsLoadingImage(true);
      try {
        const response = await fetch(
          `${process.env.BASE_URL}/api/events/image/${selectedEvent.id}`
        );
        if (!response.ok) {
          throw new Error(`Error fetching image: ${response.statusText}`);
        }
        const data = await response.json();
        setEventImage(data.image);
      } catch (error) {
        console.error("Error fetching image:", error);
      } finally {
        setIsLoadingImage(false);
      }
    };

    if (selectedEvent?.id) {
      fetchImage();
    }
  }, [selectedEvent]);

  useEffect(() => {
    const banner = eventImage
      ? eventImage
      : selectedEvent?.locatorCompanyId
        ? DEFAULT_CSP
        : selectedEvent?.partnerId
          ? DEFAULT_PARTNER_IMAGE
          : selectedEvent?.EventTypes?.name === "WEBCAST_ONLINE_PRESENTATION"
            ? DEFAULT_WEBCAST
            : selectedEvent?.EventTypes?.name?.includes("CONFERENCE")
              ? DEFAULT_CONFERENCE
              : selectedEvent?.EventTypes?.name === "USER_GROUP"
                ? DEFAULT_USER_GROUP
                : selectedEvent?.EventTypes?.name === "PRODUCT_TRAINING"
                  ? DEFAULT_TRAINING
                  : DEFAULT_IMAGE;

    setSelectedEventBanner(banner);
  }, [eventImage, selectedEvent]);

  const todayDate = getNewDate({
    isUTC0: true,
    returnDateObject: true,
    date: undefined,
  });

  const addFiveYears = getNewDate({
    date: new Date(
      todayDate.getFullYear() + 5,
      todayDate.getMonth(),
      todayDate.getDate(),
      0,
      0,
      0
    ),
    isUTC0: true,
  });

  const regions_data = regions?.map((region) => {
    return { ...region, label: region.name };
  });

  const [newEvents, setNewEvents] = useState(
    !!selectedEvent && !isCreate
      ? [
          {
            ...selectedEvent,
            startTime: getTimeFromDate({ date: selectedEvent.startDate }),
            endTime: getTimeFromDate({ date: selectedEvent.endDate }),
            products: selectedEvent.products.map((e) => {
              return { ...e, value: e.id, label: e.level3 };
            }),
            providers: selectedEvent.cloudServices.map((e) => {
              return { label: e.name, value: e.id };
            }),
            allDayEvent: selectedEvent.allDayEvent,
            image: eventImage ? selectedEventBanner : "",
          },
        ]
      : isCreate
        ? newEventsCreate || []
        : [initialEventState]
  );

  useEffect(() => {
    if (!!selectedEvent && !isCreate) {
      setNewEvents([
        {
          ...selectedEvent,
          startTime: getTimeFromDate({ date: selectedEvent.startDate }),
          endTime: getTimeFromDate({ date: selectedEvent.endDate }),
          products: selectedEvent.products.map((e) => {
            return { ...e, value: e.id, label: e.level3 };
          }),
          allDayEvent: selectedEvent.allDayEvent,
          image: eventImage ? selectedEventBanner : "",
        },
      ]);
    } else if (isCreate) {
      setNewEvents(newEventsCreate || []);
    } else {
      setNewEvents([initialEventState]);
    }
  }, [selectedEventBanner, selectedEvent, isCreate]);

  useEffect(() => {
    setTabSelected(newEvents?.length - 1);
  }, [newEvents?.length]);

  useRequiredFieldsHook({
    requiredFields,
    logicForRequiredFields: logicForRequiredFields({
      requiredFields,
      getComponentsRequiredFields: providerId
        ? getComponentRequiredFields({
            events: newEvents,
            component: "providerEvent",
          })
        : getComponentRequiredFields({
            events: newEvents,
            component: "partnerEvent",
          }),
    }),
    setRequiredFields: setRequiredFields,
    dependencies: [newEvents, tabSelected],
  });

  useValidityFieldsHook({
    fieldsValidity,
    logicForValidityFields: logicForValidityFields({
      fieldsValidity,
      getComponentsValidityFields: providerId
        ? getComponentsValidityFields({
            events: newEvents,
            component: "providerEvent",
          })
        : getComponentsValidityFields({
            events: newEvents,
            component: "partnerEvent",
          }),
    }),
    setFieldsValidity: setFieldsValidity,
    dependencies: [newEvents, tabSelected],
  });

  const [originalEvent] = useState(
    !isCreate && selectedEvent
      ? {
          ...selectedEvent,
          products: selectedEvent.products.map((e) => {
            return { ...e, value: e.id, label: e.level3 };
          }),
          providers: selectedEvent.cloudServices.map((e) => {
            return { label: e.name, value: e.id };
          }),
          allDayEvent: selectedEvent.allDayEvent,
          startTime: getTimeFromDate({ date: selectedEvent.startDate }),
          endTime: getTimeFromDate({ date: selectedEvent.endDate }),
        }
      : initialEventState
  );

  const disabled =
    (["FULL", "VIEW"].includes(getPermissionsByEventType()) &&
      isModeration &&
      !isSiteAdminPage) ||
    (isSiteAdminPage &&
      selectedEvent &&
      selectedEvent?.contentStatus !== "ACCEPTED");

  const updateEvent =
    (["REJECTED", "ACCEPTED", "REQUIRED_REVIEW"].includes(
      selectedEvent?.contentStatus
    ) &&
      getPermissionsByEventType() === "EDIT" &&
      !isCreate) ||
    (isSiteAdminPage &&
      selectedEvent?.contentStatus === "ACCEPTED" &&
      !isCreate) ||
    editEvents;

  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const products_data = products
    ?.map((l) => {
      return {
        ...l,
        label: onlyLevel2isRelevant(l?.level2) ? l.level2 : l.level3,
        value: l?.id,
      };
    })
    ?.filter((product, index, self) => {
      if (onlyLevel2isRelevant(product.level2)) {
        return index === self.findIndex((p) => p.level2 === product.level2);
      }
      return true;
    })
    ?.sort((a, b) => a.label.localeCompare(b.label));

  const providers_data = cloudServices?.map((l: CloudService) => ({
    ...l,
    label: l.name,
    value: l.id,
  }));

  const timezones_data = getTimezonesData(allTimezones);

  const index = timezones_data.findIndex(
    (el) => el.name === newEvents[tabSelected]?.timezone?.name
  );

  const elementoToMove = timezones_data.splice(index, 1)[0];
  timezones_data.unshift(elementoToMove);

  const checkAreChanges = () => {
    const changedFields = {};
    for (const prop in newEvents[tabSelected]) {
      if (prop === "products") {
        if (
          !arraysAreEqualByDiffs(
            newEvents[tabSelected]?.[prop],
            originalEvent[prop]
          )
        )
          changedFields[prop] = newEvents[tabSelected]?.[prop];
      } else if (prop === "startTime" || prop === "endTime") {
        //Original Event Start and End Time are only filled after the conversion
        if (
          originalEvent[prop]?.length > 0 &&
          newEvents[tabSelected]?.[prop] !== originalEvent[prop]
        ) {
          changedFields[prop] = newEvents[tabSelected]?.[prop];
        }
      } else if (prop === "startDate" || prop === "endDate") {
        if (
          originalEvent[prop]?.length > 0 &&
          new Date(newEvents[tabSelected]?.[prop])
            .toISOString()
            .split("T")[0] !==
            new Date(originalEvent[prop]).toISOString().split("T")[0]
        ) {
          changedFields[prop] = newEvents[tabSelected]?.[prop];
        }
      } else if (newEvents[tabSelected]?.[prop] !== originalEvent[prop]) {
        changedFields[prop] = newEvents[tabSelected]?.[prop];
      }
    }
    setAreThereChanges(
      Object.keys(changedFields).length > 0 ||
        newEvents[tabSelected]?.contentStatus === "REJECTED"
    );
  };

  //Check if there are changes on Update Event
  useEffect(() => {
    if (!isCreate) {
      checkAreChanges();
    } else {
      setAreThereChanges(validateNewEvent());
    }
  }, [newEvents]);

  const addTab = () => {
    const newEv = { ...initialEventState };
    setNewEvents([...newEvents, newEv]);
  };

  useOutsideClickMultiple([ref], () => {
    setSelectedEvent(null);
    if (isCreate) setNewEventsCreate(newEvents);
    queryParams({ del: ["eventId"] });
  });

  const validateNewEvent = () => {
    let isInvalid = false;
    newEvents.forEach((event) => {
      if (
        event.title.trim().length === 0 ||
        event.type.length === 0 ||
        event.tagline.trim().length === 0 ||
        event.tagline?.trim()?.split(" ").length > MAX_WORDS_TAGLINE ||
        event.startTime.length === 0 ||
        event.description.trim().length === 0 ||
        (providerId
          ? event.providers?.length === 0
          : event.products?.length === 0) ||
        !event.regionId ||
        !event.timezone
      ) {
        isInvalid = true;
      }
    });

    return !isInvalid;
  };

  const deleteTab = (tab) => {
    // setTabSelected(tab - 1);
    setNewEvents((prevItems) => {
      const updatedItems = prevItems.filter((item, index) => index !== tab);
      return updatedItems;
    });
  };

  const onChangeDate = (date, id) => {
    if (!date || !id) return;
    const data = [...newEvents];
    [...data][tabSelected][id] = getNewDate({
      date,
      getISODateFromUsersTimezone: true,
      usersTimezoneLocation: usersTimezoneLocation,
      removeTimezone: true,
    });

    setNewEvents([...data]);

    validateDates();
    validateTime();
  };

  function refetchEvents() {
    queryClient.invalidateQueries({
      queryKey: [
        partnerId
          ? TAGS.partnerEvents(partnerId)
          : providerId
            ? TAGS.providerEvents(providerId)
            : TAGS.events,
      ],
    });
  }

  const handleCancelEvent = () => {
    changeSetOpen(false);
    setSelectedEvent(null);
    setNewEvents([initialEventState]);
    if (isCreate) setNewEventsCreate([initialEventState]);
    refetchEvents();
    queryParams({ del: ["eventId"] });
  };

  const handleOnDrop = (files) => {
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = async function (e) {
      if (files[0].size >= 760000) {
        toast.error(
          "Please use images with 6k resolution and lower, and 760Kb or less"
        );
        return;
      } else {
        const file = await resizedImage(e, files, 800, 800);

        const data = [...newEvents];
        data[tabSelected].image = file;
        setNewEvents([...data]);
      }
    }.bind(this);
  };

  const handleUpdateEvent = async () => {
    const eventsToUpdate = newEvents.map((el) => {
      return {
        ...el,
        partnerId: partnerId,
        startDate: addTimeToDate({ date: el.startDate, time: el.startTime }),
        endDate: addTimeToDate({ date: el.endDate, time: el.endTime }),
        timezone: el.timezone?.hasOwnProperty("value")
          ? newEvents[tabSelected]?.timezone.value
          : newEvents[tabSelected]?.timezone,
      };
    });

    setNewEvents(eventsToUpdate);

    if (newEvents[tabSelected]?.["title"].length < 2) {
      return toast.error(t("event_min_characters"));
    }

    const changedFields = {};

    for (const prop in eventsToUpdate[tabSelected]) {
      if (prop === "products") {
        if (
          !arraysAreEqualByDiffs(
            eventsToUpdate[tabSelected][prop],
            originalEvent[prop]
          )
        )
          changedFields[prop] = eventsToUpdate[tabSelected][prop];
      } else if (eventsToUpdate[tabSelected][prop] !== originalEvent[prop]) {
        if (prop === "startTime" || prop === "endTime") {
          changedFields[prop === "startTime" ? "startDate" : "endDate"] =
            eventsToUpdate[tabSelected][
              prop === "startTime" ? "startDate" : "endDate"
            ];
        } else {
          changedFields[prop] = eventsToUpdate[tabSelected][prop];
        }
      }
    }

    if (Object.keys(changedFields).length > 0) {
      mutateUpdate({
        id: eventsToUpdate[tabSelected].id,
        event: changedFields,
        isSiteAdminPage: getPermissionsByEventType() === "FULL",
        allDay: eventsToUpdate[tabSelected]["allDayEvent"],
      });
    } else {
      toast.error(`No changes were made.`);
    }

    queryParams({ del: ["eventId"] });
  };

  const handleCreateEvent = async () => {
    setShowErrors(true);

    if (
      checkRequiredFields({
        requiredFields,
        setTabSelected,
      }) ||
      checkValidityFields({
        fieldsValidity,
        setTabSelected,
      })
    )
      return;

    setShowErrors(false);

    if (newEvents.some((el) => el.title.length < 2)) {
      return toast.error(t("event_min_characters"));
    }

    mutateCreate();
  };

  const { mutate: mutateCreate, isPending: isLoadingCreate } = useMutation({
    mutationFn: async () => {
      const eventsToCreate = newEvents.map((el) => {
        return {
          ...el,
          partnerId: partnerId,
          providerId: providerId,
          type: el.type?.id,
          startDate: addTimeToDate({ date: el.startDate, time: el.startTime }),
          endDate: addTimeToDate({ date: el.endDate, time: el.endTime }),
          timezone: el.timezone?.hasOwnProperty("value")
            ? newEvents[tabSelected]?.timezone.value
            : newEvents[tabSelected]?.timezone,
        };
      });

      setNewEvents(eventsToCreate);

      const { ok, message, data } = await createEvents({
        partnerId: partnerId,
        providerId: providerId,
        newEvents: eventsToCreate,
        isSiteAdminPage: isSiteAdminPage,
      });

      if (!ok) throw new Error(message);

      return { ok, message, data };
    },
    onSuccess: async () => {
      toast.success(
        isSiteAdminPage
          ? `Event(s) created.`
          : `Event(s) created and sent to moderation.`
      );

      changeSetOpen(false);
      refetchEvents();
      setSelectedEvent(null);
      setNewEvents([]);
      setNewEventsCreate([initialEventState]);

      try {
        const url = providerId
          ? `/api/sendEmail/provider/events/new`
          : `/api/sendEmail/events/new`;

        const res = await axios.post(url, {
          partnerName,
          partnerSlug,
        });

        if (res.status !== 200) {
          toast.error(
            `Error while sending email. Please contact the administrator.`
          );
        }
      } catch (error) {
        toast.warning("Email notification was not sent.");
      }
    },
    onError: () => {
      toast.error("Create Event(s)", {
        description: "There was an error create the Event(s)",
      });
      setNewEvents([]);
      setNewEventsCreate([initialEventState]);
    },
  });

  const { mutate: mutateUpdate, isPending: isLoadingUpdate } = useMutation({
    mutationFn: async (body: {
      id: number;
      event: any;
      isSiteAdminPage: boolean;
      allDay: any;
    }) => {
      const { ok, message, data } = await updateEvents(body);
      if (!ok) throw new Error(message);
      return { ok, message, data };
    },
    onSuccess: async () => {
      refetchEvents();
      changeSetOpen(false);
      toast.success("Update Event", {
        description: `Event was updated successfully${
          getPermissionsByEventType() === "FULL"
            ? ""
            : " and sent to moderation"
        }.`,
      });
      setSelectedEvent(null);

      const sendEmail = originalEvent.contentStatus !== "REQUIRED_REVIEW";

      if (!sendEmail) return;
      try {
        const url = originalEvent.partnerId
          ? `/api/sendEmail/events/requiredReview`
          : `/api/sendEmail/provider/events/requiredReview`;

        const res = await axios.post(url, {
          title: originalEvent.title,
          partnerName,
          partnerSlug,
        });

        if (res?.status !== 200) {
          toast.error(
            `Error while sending email. Please contact the administrator.`
          );
        }
      } catch (error) {
        toast.warning("Email notification was not sent.");
      }
    },
    onError: (error) => {
      toast.error("Update Event", {
        description: `There was an error uploading the event.`,
      });
    },
  });

  const { mutate: mutateDelete, isPending: isLoadingDelete } = useMutation({
    mutationFn: async () => {
      const { ok, message, data } = await deleteEventById({
        id: Number(newEvents[tabSelected]?.id),
      });
      if (!ok) throw new Error(message);

      return { ok, message, data };
    },
    onSuccess: () => {
      refetchEvents();
      toast.success("Delete event", {
        description: `Event was deleted successfully.`,
      });
      changeSetOpen(false);
      setSelectedEvent(null);
      queryParams({ del: ["eventId"] });
    },
    onError: () => {
      toast.error(`There was an error deleting the event.`);
      changeSetOpen(false);
      setSelectedEvent(null);
      queryParams({ del: ["eventId"] });
    },
  });

  const validateDates = () => {
    if (
      getNewDate({ isUTC0: true, date: newEvents[tabSelected]?.startDate }) <
      getNewDate({ isUTC0: true, date: undefined })
    ) {
      const data = [...newEvents];
      data[tabSelected].startDate = getNewDate({
        isUTC0: true,
        date: undefined,
      });
      setNewEvents([...data]);
    }

    if (
      getNewDate({ isUTC0: true, date: newEvents[tabSelected]?.endDate }) <
      getNewDate({ isUTC0: true, date: undefined })
    ) {
      const data = [...newEvents];
      data[tabSelected].endDate = getNewDate({ isUTC0: true, date: undefined });
      setNewEvents([...data]);
    }

    if (
      getNewDate({
        date: newEvents[tabSelected]?.endDate,
        usersTimezoneLocation,
        getISODateFromUsersTimezone: true,
      }) <
      getNewDate({
        date: newEvents[tabSelected]?.startDate,
        usersTimezoneLocation,
        getISODateFromUsersTimezone: true,
      })
    ) {
      const data = [...newEvents];
      data[tabSelected].endDate = newEvents[tabSelected]?.startDate;
      setNewEvents([...data]);
    }
  };

  const validateTime = () => {
    if (
      !newEvents[tabSelected]?.allDayEvent &&
      newEvents[tabSelected]?.startTime
    ) {
      const startTime = addTimeToDate({
        date: newEvents[tabSelected]?.startDate,
        time: newEvents[tabSelected]?.startTime,
      });

      const [hour, minutes] =
        getHoursAndMinutesFromISODate(startTime).split(":");

      const timeNow = getNewDate({
        getCurrentDateAndTime: true,
        date: undefined,
      });
      const [hoursNow, minutesNow] =
        getHoursAndMinutesFromISODate(timeNow).split(":");

      if (
        getNewDate({
          isUTC0: true,
          date: newEvents[tabSelected]?.startDate,
        }) === getNewDate({ isUTC0: true, date: undefined }) &&
        startTime &&
        startTime < timeNow
      ) {
        const startTimeplus2 = `${Number(hoursNow) + 1}:${minutesNow}`;
        const data = [...newEvents];
        data[tabSelected].startTime = getHoursAndMinutesFromISODate(timeNow);
        data[tabSelected].endTime = startTimeplus2;
        setNewEvents([...data]);
      }
      if (
        getNewDate({
          isUTC0: true,
          date: newEvents[tabSelected]?.startDate,
        }) ===
          getNewDate({ isUTC0: true, date: newEvents[tabSelected]?.endDate }) &&
        newEvents[tabSelected]?.startTime &&
        (newEvents[tabSelected]?.startTime > newEvents[tabSelected]?.endTime ||
          !newEvents[tabSelected]?.endTime)
      ) {
        const startTimeplus1 = `${Number(hour) + 1}:${minutes}`;
        const data = [...newEvents];
        data[tabSelected].endTime = startTimeplus1;
        setNewEvents([...data]);
      }
    }
  };

  if (
    getNewDate({
      isUTC0: true,
      date: newEvents[tabSelected]?.startDate,
    }) <
    getNewDate({
      date: undefined,
      isUTC0: true,
    })
  )
    newEvents[tabSelected].startDate = getNewDate({
      date: new Date(),
      usersTimezoneLocation,
      getISODateFromUsersTimezone: true,
    });

  if (
    getNewDate({
      isUTC0: true,
      date: newEvents[tabSelected]?.endDate,
    }) <
    getNewDate({
      date: undefined,
      isUTC0: true,
    })
  )
    newEvents[tabSelected].endDate = getNewDate({
      date: new Date(),
      usersTimezoneLocation,
      getISODateFromUsersTimezone: true,
    });

  function ColorsDrop(option) {
    return (
      <div
        className={styles.color_select}
        style={{ backgroundColor: `${option.color}` }}
      >
        {newEvents[tabSelected]?.color === option.color ? (
          <ColorSymbol />
        ) : null}
        <span
          style={{
            marginLeft: "5px",
            fontWeight: "bold",
            verticalAlign: "middle",
          }}
        >
          {" "}
        </span>
      </div>
    );
  }

  return (
    <>
      {isLoadingCreate ||
      isLoadingUpdate ||
      isLoadingDelete ||
      isLoadingImage ? (
        <Loader />
      ) : null}
      {isOpen ? (
        <Dialog onOpenChange={changeSetOpen} open={isOpen}>
          <DialogContent
            className={`${styles.modal_content} py-0 sm:min-w-[50vw] overflow-y-hidden`}
            closeCallback={() => {
              setSelectedEvent(null);
              setNewEvents([initialEventState]);
              setNewEventsCreate([initialEventState]);
              queryParams({ del: ["eventId"] });
              changeSetOpen(false);
            }}
            disabledClickOutside={true}
            onPointerDownOutside={() => {
              setSelectedEvent(null);
              setNewEvents([initialEventState]);
              setNewEventsCreate([initialEventState]);
              queryParams({ del: ["eventId"] });
              changeSetOpen(false);
            }}
          >
            <DialogHeader>
              <DialogTitle
                className="text-left capitalize text-[#2360A0] leading-[30px] text-[22px] font-[350]"
                icon={isCreate ? "create_icon" : "calendar_icon"}
              >
                {isCreate ? (
                  <div className={styles.header_grid}>
                    <div className={styles.title}>{t("create_event")}</div>
                  </div>
                ) : null}
                {isModeration || editEvents ? (
                  <div className={styles.header_grid}>
                    <div className="font-[350] text-[22px] text-[#1F384C] trailling-[30px]">
                      {selectedEvent?.title}
                    </div>
                  </div>
                ) : null}
              </DialogTitle>
              {isCreate ? (
                <DialogDescription>{t("create_event_descr")}</DialogDescription>
              ) : null}
            </DialogHeader>
            <div className={styles.body} onScroll={() => setPopupOpen(false)}>
              {isModeration || editEvents || (isSiteAdminPage && !isCreate) ? (
                <div className={styles.banner_and_logo}>
                  <img alt="background-img" src={selectedEventBanner} />
                  <img
                    alt="event-logo"
                    className={styles.logo}
                    src={
                      selectedEvent?.Partner?.logo ||
                      selectedEvent?.LocatorCompany?.logo ||
                      "/images/broadcom-icon.png"
                    }
                  />
                  {selectedEvent?.contentStatus === "REQUIRED_REVIEW" ||
                  selectedEvent?.contentStatus === "REJECTED" ? (
                    <div
                      className={
                        selectedEvent?.contentStatus === "REQUIRED_REVIEW"
                          ? styles.status_mod
                          : styles.status_rej
                      }
                    >
                      {selectedEvent?.contentStatus === "REQUIRED_REVIEW"
                        ? t("moderation")
                        : t("rejected")}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {(!isModeration && !editEvents && !isSiteAdminPage) ||
              (isSiteAdminPage && isCreate) ? (
                <div className={isCreate ? styles.tabs : ""}>
                  {newEvents.map((event, index) => (
                    <div
                      className={
                        tabSelected === index
                          ? styles.tab_selected
                          : styles.tab_not_selected
                      }
                      key={`tab_${index}`}
                      onClick={() => {
                        setTabSelected(index);
                      }}
                    >
                      {`${t("event")} ${index > 0 ? `(${index})` : ""}`}
                      {index > 0 ? (
                        <DeleteButtonPopup
                          actionYes={(index) => deleteTab(index)}
                          classNames={{
                            buttonWithIcon:
                              tabSelected === index
                                ? "w-[20px] h-[20px] !bg-[#fafafb] !shadow-none"
                                : "w-[20px] h-[20px] !bg-[#ebebec] !shadow-none",
                            buttonWithIconHover:
                              "w-[20px] h-[20px] !shadow-none",
                            iconStylesOpen: "text-[#EB5757]",
                            iconStylesClose: "text-[#2360A0]",
                            buttonCancel:
                              "bg-black hover:bg-black text-white text-xs p-2 h-auto rounded-sm",
                            buttonYes:
                              "bg-[#EB5757] hover:bg-[#EB5757] text-white text-xs p-2 h-auto rounded-sm",
                          }}
                          id={index}
                          open={popupOpen}
                          question={
                            <span className="!bg-[#1F384C] !text-white pb-4">
                              {t("r_u_sure_delete")}{" "}
                              <span className="!text-[#EB5757] underline">
                                {t("delete_q")}
                              </span>{" "}
                              {t("this_event_q")}
                            </span>
                          }
                          setOpen={setPopupOpen}
                        />
                      ) : null}
                    </div>
                  ))}
                  <div className={styles.plus} onClick={addTab}>
                    +
                  </div>
                </div>
              ) : null}
              <div
                className={styles.input_wrapper}
                style={
                  isModeration
                    ? {
                        backgroundColor: "white",
                        marginTop: "-30px",
                      }
                    : {}
                }
              >
                <div
                  className={`${styles.first_line} grid_container two_columns`}
                >
                  <InputTextPV
                    disabled={disabled}
                    hideRequiredMessage
                    id="event_title"
                    key={`title_${tabSelected}`}
                    label={t("event_title")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].title = e?.target?.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("event_title")}
                    required
                    uncontrolledError={
                      showErrors
                        ? checkError({
                            field: `event_title_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    value={newEvents[tabSelected]?.title}
                  />
                  <div className="mt-8">
                    <SelectPV
                      customStyles={{
                        valueContainer: (provided) => ({
                          ...provided,
                          width: "100px",
                          marginTop: "6px",
                          justifyContent: "center",
                          svg: {
                            display: "none",
                          },
                        }),
                      }}
                      data={colors}
                      disabled={disabled}
                      formatOptionLabel={(color) => ColorsDrop(color)}
                      fullWidth
                      hasColumns
                      id="event_color"
                      isMultiple={false}
                      isSearchable={false}
                      key={`color_${tabSelected}`}
                      onChange={(e) => {
                        const data = [...newEvents];
                        data[tabSelected].color = e.value;
                        setNewEvents([...data]);
                      }}
                      placeholder={t("Color")}
                      value={{ color: newEvents[tabSelected]?.color }}
                    />
                  </div>
                </div>
                <div className={`${styles.default_line} grid_container`}>
                  <SelectPV
                    customErrorMessage={
                      showErrors
                        ? checkError({
                            field: `event_type_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    data={eventTypes}
                    disabled={disabled}
                    fullWidth
                    id="event_type"
                    isMultiple={false}
                    key={`event_type${tabSelected}`}
                    label={t("event_type")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].type = e;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("event_type")}
                    required
                    value={
                      newEvents[tabSelected]?.type
                        ? {
                            value: newEvents[tabSelected]?.type.name,
                            label: newEvents[tabSelected]?.type.label,
                          }
                        : null
                    }
                  />
                </div>
                <div className={`${styles.default_line} grid_container `}>
                  <InputTextPV
                    description={t("10_words_less")}
                    disabled={disabled}
                    id="event_tagline"
                    key={`tagline_${tabSelected}`}
                    label={t("tagline")}
                    maxWords={MAX_WORDS_TAGLINE}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].tagline = e?.target?.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("tagline")}
                    required
                    uncontrolledError={
                      showErrors
                        ? checkError({
                            field: `event_tagline_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    value={newEvents[tabSelected]?.tagline}
                  />
                </div>
                <div className={`${styles.default_line} grid_container `}>
                  <InputMarkdown
                    disabled={disabled}
                    id="event_description"
                    key={`description_${tabSelected}`}
                    label={t("description")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].description = e?.target?.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("description")}
                    required
                    uncontrolledError={
                      showErrors
                        ? checkError({
                            field: `event_description_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    value={newEvents[tabSelected]?.description}
                  />
                </div>
                <div className={`${styles.default_line} grid_container `}>
                  <SelectPV
                    data={timezones_data}
                    disabled={disabled}
                    id="timezone"
                    isMultiple={false}
                    isSearchable={true}
                    key={`timezone${tabSelected}`}
                    label={t("timezone")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].timezone = e;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("timezone")}
                    required
                    value={
                      !newEvents[tabSelected]?.timezone.hasOwnProperty("value")
                        ? {
                            label: newEvents[tabSelected]?.timezone
                              ? newEvents[tabSelected]?.timezone
                              : null,
                            value: newEvents[tabSelected]?.timezone
                              ? newEvents[tabSelected]?.timezone
                              : null,
                          }
                        : newEvents[tabSelected]?.timezone
                          ? newEvents[tabSelected]?.timezone
                          : null
                    }
                  />
                </div>
                <div
                  className={`${styles.default_line} grid_container two_columns`}
                >
                  <InputDate
                    disabled={disabled}
                    error={
                      showErrors
                        ? checkError({
                            field: `start_date_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    id="startDate"
                    key={`startdate_${tabSelected}`}
                    label={t("start_date")}
                    max={getNewDate({
                      date: addFiveYears,
                      returnDateObject: true,
                    })}
                    min={getNewDate({
                      date: new Date(),
                      usersTimezoneLocation:
                        newEvents[tabSelected]?.timezone.name,
                      getDateObjectFromUsersTimezone: true,
                    })}
                    onChange={onChangeDate}
                    placeholder={t("start_date")}
                    required
                    value={newEvents[tabSelected]?.startDate}
                  />
                  <InputDate
                    disabled={disabled}
                    error={
                      showErrors
                        ? checkError({
                            field: `end_date_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    id="endDate"
                    key={`enddate_${tabSelected}`}
                    label={t("end_date")}
                    max={getNewDate({
                      date: addFiveYears,
                      returnDateObject: true,
                    })}
                    min={getNewDate({
                      date: newEvents[tabSelected]?.startDate,
                      returnDateObject: true,
                    })}
                    onChange={onChangeDate}
                    placeholder={t("end_date")}
                    required
                    value={newEvents[tabSelected]?.endDate}
                  />
                </div>
                <div
                  className={`${styles.default_line} grid_container two_columns`}
                >
                  <InputTextPV
                    disabled={disabled || newEvents[tabSelected]?.allDayEvent}
                    hideRequiredMessage
                    id="event_startTime"
                    key={`starttime_${tabSelected}`}
                    label={t("start_time")}
                    onBlur={validateTime}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].startTime = e.target.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("start_time")}
                    required
                    type="time"
                    uncontrolledError={
                      showErrors
                        ? checkError({
                            field: `start_time_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    value={newEvents[tabSelected]?.startTime}
                  />
                  <InputTextPV
                    disabled={disabled || newEvents[tabSelected]?.allDayEvent}
                    hideRequiredMessage
                    id="event_endTime"
                    key={`endtime_${tabSelected}`}
                    label={t("end_time")}
                    onBlur={validateTime}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].endTime = e.target.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("end_time")}
                    required
                    type="time"
                    uncontrolledError={
                      showErrors
                        ? checkError({
                            field: `end_time_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    value={newEvents[tabSelected]?.endTime}
                  />
                </div>
                <div
                  className={`${styles.default_line} ${styles.all_day} grid_container`}
                >
                  <Checkbox
                    checked={newEvents[tabSelected]?.allDayEvent}
                    disabled={disabled}
                    id="event_allday"
                    key={`all_day_${tabSelected}`}
                    label={t("all_day")}
                    onCheckedChange={() => {
                      const data = [...newEvents];
                      data[tabSelected].allDayEvent =
                        !newEvents[tabSelected]?.allDayEvent;
                      data[tabSelected].startTime = newEvents[tabSelected]
                        .allDayEvent
                        ? "08:00:00.000Z"
                        : "";
                      data[tabSelected].endTime = newEvents[tabSelected]
                        .allDayEvent
                        ? "18:00:00.000Z"
                        : "";
                      setNewEvents([...data]);
                    }}
                  />
                </div>
                <div className={`${styles.default_line} grid_container`}>
                  <SelectPV
                    customErrorMessage={
                      showErrors
                        ? checkError({
                            field: providerId
                              ? `cloud_services_${tabSelected}`
                              : `products_${tabSelected}`,

                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    data={providerId ? providers_data : products_data}
                    disabled={disabled}
                    id={providerId ? "cloudServices" : "products"}
                    isMultiple={true}
                    key={`prod_${tabSelected}`}
                    label={providerId ? t("cloud_services") : t("products")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      if (providerId) {
                        data[tabSelected].cloudServices = e;
                      } else {
                        data[tabSelected].products = e;
                      }
                      setNewEvents([...data]);
                    }}
                    placeholder={
                      providerId ? t("cloud_services") : t("products")
                    }
                    required
                    value={
                      providerId
                        ? newEvents[tabSelected]?.cloudServices
                        : newEvents[tabSelected]?.products
                    }
                  />
                </div>
                <div className={`${styles.default_line} grid_container `}>
                  <InputTextPV
                    disabled={disabled}
                    hideRequiredMessage
                    id="event_location"
                    key={`location_${tabSelected}`}
                    label={t("location")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].location = e?.target?.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("location")}
                    value={newEvents[tabSelected]?.location}
                  />
                  <SelectPV
                    customErrorMessage={
                      showErrors
                        ? checkError({
                            field: `region_id_${tabSelected}`,
                            requiredFields,
                            fieldsValidity,
                          })
                        : ""
                    }
                    data={regions_data}
                    disabled={disabled}
                    id="regionId"
                    isMultiple={false}
                    key={`regions${tabSelected}`}
                    label={t("region")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].regionId = e;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("region")}
                    required
                    value={
                      newEvents[tabSelected]?.regionId
                        ? {
                            value: newEvents[tabSelected]?.regionId.name,
                            label: newEvents[tabSelected]?.regionId.label,
                          }
                        : null
                    }
                  />
                </div>
                <div className={`${styles.default_line} grid_container `}>
                  <InputTextPV
                    disabled={disabled}
                    hideRequiredMessage
                    id="event_link"
                    key={`link_${tabSelected}`}
                    label={t("link")}
                    onChange={(e) => {
                      const data = [...newEvents];
                      data[tabSelected].link = e?.target?.value;
                      setNewEvents([...data]);
                    }}
                    placeholder={t("link")}
                    value={newEvents[tabSelected]?.link}
                  />
                </div>
                <div className={`${styles.dropzone_wrapper} `}>
                  <div className={styles.label}>
                    <label className={styles.label}>
                      {t("event_image")}{" "}
                      <span className={styles.hasNote}>
                        ({t("event_image_info")})
                      </span>
                    </label>
                  </div>
                  <Dropzone
                    accept={[".jpeg", ".png", ".jpg"]}
                    disabled={disabled}
                    onDrop={(acceptedFiles) => handleOnDrop(acceptedFiles)}
                    ref={dropzoneRef}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section className={styles.dropzone}>
                        {newEvents[tabSelected]?.image !== "" ? (
                          <>
                            <img
                              alt="event-logo"
                              className={`${styles.delete_image} ${
                                disabled ? styles.close_icon_disabled : ""
                              }`}
                              onClick={() => {
                                const data = [...newEvents];
                                data[tabSelected].image = "";
                                setNewEvents([...data]);
                              }}
                              src={DELETE_IMAGE}
                            />
                            <img
                              className={`${styles.drop_image_filled} `}
                              src={newEvents[tabSelected]?.image || ""}
                              {...getRootProps()}
                              alt="event_image"
                            />
                            <input {...getInputProps()} />
                          </>
                        ) : (
                          <div
                            className={styles.drop_image}
                            {...getRootProps()}
                          >
                            <input
                              id="logo"
                              type="file"
                              {...getInputProps({})}
                            />
                            <img src="/images/upload-cloud.svg" />
                            <span>{t("drag_drop")}</span>
                            <p>{t("select_files")}</p>
                          </div>
                        )}
                      </section>
                    )}
                  </Dropzone>
                </div>
                {updateEvent ? (
                  <div className={styles.delete_event}>
                    <div>
                      <Button
                        className="p-2"
                        icon="trash"
                        iconStyles="w-5 h-5"
                        onClick={() => {
                          setDeleteOpen(true);
                        }}
                        variant="secondary"
                      />
                      <span
                        style={{ color: "#EB5757", margin: "0 5px 0 10px" }}
                      >
                        {t("click_delete_event")}
                      </span>
                      <span> {t("action_undone")}</span>
                    </div>
                  </div>
                ) : null}
              </div>
              {getPermissionsByEventType() === "EDIT" && updateEvent ? (
                <div className="h-[62px] w-full lg:w-[80%] grid grid-cols-1 sm:grid-cols-2 items-start gap-8">
                  <Button
                    className="box-border group cursor-pointer inline-flex items-center justify-center gap-2 rounded-[7px] text-base tracking-[1px] font-light transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed border-0 bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-[#97999b] h-[62px] px-4 py-2"
                    disabled={!areThereChanges}
                    onClick={() => handleUpdateEvent()}
                  >
                    {t("save")}
                  </Button>
                  <Button
                    className="box-border group cursor-pointer inline-flex items-center justify-center gap-2 rounded-[7px] text-base tracking-[1px] font-light transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed border-0 bg-transparent text-[#1F384C] hover:bg-accent hover:text-primary-hover h-[62px] px-4 py-2"
                    onClick={() => handleCancelEvent()}
                    variant="ghost"
                  >
                    {t("cancel")}
                  </Button>
                </div>
              ) : null}
            </div>
            {!isCreate &&
            (isModeration || providerId
              ? MOD_PERMISSIONS.includes(userPermissions?.providerEvents)
              : MOD_PERMISSIONS.includes(userPermissions?.partnerEvetns)) ? (
              <EventModeration
                changeSetOpen={changeSetOpen}
                isOpen={modContentIsOpen}
                partnerSlug={partnerSlug}
                refetchEvents={refetchEvents}
                rerender={rerender}
                setIsOpen={setModContentIsOpen}
                setRerender={setRerender}
                userPermissions={userPermissions}
              />
            ) : null}
            {isCreate ? (
              <DialogFooter>
                <Button onClick={() => handleCreateEvent()}>
                  {newEvents?.length > 1
                    ? `${t("create")} ${t("events")}`
                    : t("create_event")}
                </Button>
                <Button onClick={() => handleCancelEvent()} variant="ghost">
                  {t("cancel")}
                </Button>
              </DialogFooter>
            ) : null}
            {getPermissionsByEventType() === "FULL" && updateEvent ? (
              <div className="h-[62px] w-full lg:w-[80%] grid grid-cols-1 sm:grid-cols-2 items-start gap-8">
                <Button
                  className="box-border group cursor-pointer inline-flex items-center justify-center gap-2 rounded-[7px] text-base tracking-[1px] font-light transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed border-0 bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-[#97999b] h-[62px] px-4 py-2"
                  onClick={() => handleUpdateEvent()}
                >
                  {t("save")}
                </Button>
                <Button
                  className="box-border group cursor-pointer inline-flex items-center justify-center gap-2 rounded-[7px] text-base tracking-[1px] font-light transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed border-0 bg-transparent text-[#1F384C] hover:bg-accent hover:text-primary-hover h-[62px] px-4 py-2"
                  onClick={() => handleCancelEvent()}
                  variant="ghost"
                >
                  {t("cancel")}
                </Button>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}
      {isDeleteOpen ? (
        <ConfirmDeleteModal
          description={`${t("all_info")}`}
          handleDelete={() => mutateDelete()}
          isOpen={isDeleteOpen}
          objectType="event"
          question={`${t("sure_delete")} ${t("event")}`}
          setOpen={setDeleteOpen}
        />
      ) : null}
    </>
  );
}
